<template>
  <v-card>
    <v-card-item v-if="year">
      <v-card-title> Year {{ year?.year }} </v-card-title>
    </v-card-item>

    <v-card-text>
      <v-row>
        <v-col cols="2">
          <v-sheet rounded="lg">
            <v-list-item
              v-for="(item, index) in listItems"
              :key="index"
              :class="{ active: currentTab === item.name }"
              @click="selectTab(item.name)">
              {{ item.label }}
            </v-list-item>
          </v-sheet>
        </v-col>

        <v-col>
          <v-sheet min-height="70vh" rounded="lg">
            <component
              :is="currentComponent"
              v-if="currentComponent && currentProps"
              v-bind="currentProps"
              @snack="throwSnack" />
          </v-sheet>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { SNACKSTATUS } from '@/stores/util';
import { useSnack } from '@/helpers';
import { useYearStore } from '@/stores';
import YearInfoSubView from './subviews/YearInfoSubView.vue';
import { YearDTO } from '@/dtos';
import YearProductsSubView from './subviews/YearProductsSubView.vue';
import BranchYearTeamsListSubView from './subviews/BranchYearTeamsListSubView.vue';
import CriteriaSubView from './subviews/CriteriaSubView.vue';
import EntryFormSubView from './subviews/EntryFormSubView.vue';
import RubricFormSubView from './subviews/RubricFormSubView.vue';

const emit = defineEmits<{
  (e: 'snack', objType: string, snackType: SNACKSTATUS, err?: string): void;
}>();

const { throwSnack } = useSnack(emit);
const route = useRoute();
const router = useRouter();
const yearStore = useYearStore();

const yearId = ref(
  Array.isArray(route.params.yearId) ? route.params.yearId[0] : (route.params.yearId ?? ''),
);

const query = yearStore.getYearByIdQuery(yearId);
const year = computed(() => {
  return query.data.value ?? new YearDTO();
});

const componentProps = computed(() => {
  if (!year.value) return null;
  return { year: new YearDTO(year.value) };
});

const listItems = computed(() => [
  {
    name: 'edit',
    label: 'Info',
    component: YearInfoSubView,
    props: componentProps.value,
  },
  {
    name: 'products',
    label: 'Products',
    component: YearProductsSubView,
    props: componentProps.value,
  },
  {
    name: 'branches',
    label: 'Branches',
    component: BranchYearTeamsListSubView,
    props: componentProps.value,
  },
  {
    name: 'criteria',
    label: 'Criteria',
    component: CriteriaSubView,
    props: componentProps.value,
  },
  {
    name: 'entry',
    label: 'Entry Form',
    component: EntryFormSubView,
    props: componentProps.value,
  },
  {
    name: 'rubric',
    label: 'Rubric',
    component: RubricFormSubView,
    props: componentProps.value,
  },
]);

const currentTab = ref(route.params.tabName || 'edit');

const currentTabItem = computed(() => listItems.value.find((i) => i.name === currentTab.value));

const currentComponent = computed(() => currentTabItem.value?.component || null);
const currentProps = computed(() => currentTabItem.value?.props || null);

function selectTab(tabName: string) {
  currentTab.value = tabName;
}

watch(
  currentTab,
  (newTab) => {
    router.push({
      name: 'AdminYearEdit',
      params: { yearId: yearId.value, tabName: newTab },
    });
  },
  { immediate: true },
);
</script>
